import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, Waves, Clock, Users, MapPin, Star, TrendingUp, Activity, ChevronRight, Flame, Zap, Share2, BadgeCheck } from 'lucide-react';
import PageShell from '../components/PageShell';
import ReferralCard from '../components/ReferralCard';
import LeaderboardCard from '../components/LeaderboardCard';
import SEO from '../components/SEO';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../utils/api';


const TABS = [
  { id: 'feed', label: 'Feed', icon: Activity },
  { id: 'leaderboard', label: 'Rankings', icon: Trophy },
  { id: 'referral', label: 'Referrals', icon: Flame },
  { id: 'spots', label: 'Hot Spots', icon: TrendingUp },
];

function TimeAgo({ dateStr }) {
  if (!dateStr) return null;
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return <span>just now</span>;
  if (diff < 3600) return <span>{Math.floor(diff / 60)}m ago</span>;
  if (diff < 86400) return <span>{Math.floor(diff / 3600)}h ago</span>;
  if (diff < 604800) return <span>{Math.floor(diff / 86400)}d ago</span>;
  return <span>{d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>;
}

function Avatar({ name, picture, size = 'md', isPro = false }) {
  const sizes = { sm: 'w-6 h-6 text-[8px]', md: 'w-8 h-8 text-[10px]', lg: 'w-10 h-10 text-xs' };
  const cls = sizes[size] || sizes.md;
  const ringCls = isPro ? 'ring-2 ring-amber-400/70 shadow-[0_0_8px_rgba(251,191,36,0.3)]' : 'border border-white/10';
  if (picture) {
    return <img src={picture} alt="" className={`${cls} rounded-full object-cover ${ringCls}`} />;
  }
  return (
    <div className={`${cls} rounded-full bg-muted ${ringCls} flex items-center justify-center font-bold`}>
      {(name || '?')[0].toUpperCase()}
    </div>
  );
}

function StarsDisplay({ rating, size = 'w-2.5 h-2.5' }) {
  if (!rating || rating <= 0) return null;
  return (
    <div className="flex gap-px">
      {[1, 2, 3, 4, 5].map(r => (
        <Star key={r} className={`${size} ${r <= rating ? 'text-secondary fill-secondary' : 'text-muted/30'}`} />
      ))}
    </div>
  );
}

function StatCard({ icon: Icon, value, label, color, testId }) {
  return (
    <div className="bg-bg-subtle/60 border border-white/5 rounded-xl px-3 py-2.5 text-center" data-testid={testId}>
      <Icon className={`w-3.5 h-3.5 mx-auto mb-1 ${color}`} />
      <p className="text-lg font-bold leading-none">{value}</p>
      <p className="text-[8px] text-muted-foreground mt-1 uppercase tracking-wider">{label}</p>
    </div>
  );
}

function RankCard({ entry, rank }) {
  const badgeColors = {
    1: 'bg-secondary border-secondary text-secondary-foreground',
    2: 'bg-muted-foreground/60 border-muted-foreground/30 text-white',
    3: 'bg-accent border-accent text-accent-foreground',
  };
  const badgeColor = badgeColors[rank] || 'bg-muted/60 border-white/10 text-muted-foreground';
  // Height scales down from #1 (tallest) to #7
  const heights = { 1: 'pt-3 pb-2', 2: 'pt-2 pb-1.5', 3: 'pt-2 pb-1.5', 4: 'pt-1.5 pb-1', 5: 'pt-1.5 pb-1', 6: 'pt-1 pb-1', 7: 'pt-1 pb-1' };
  const isTop = rank <= 3;
  const avatarSize = rank === 1 ? 'lg' : isTop ? 'md' : 'sm';
  const nameSize = rank === 1 ? 'text-xs' : isTop ? 'text-[10px]' : 'text-[9px]';
  const subSize = isTop ? 'text-[8px]' : 'text-[7px]';
  const badgeSize = isTop ? 'w-4 h-4 text-[8px]' : 'w-3.5 h-3.5 text-[7px]';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: rank * 0.04 }}
      className={`flex-1 min-w-0 flex flex-col items-center bg-bg-subtle/50 border border-white/[0.04] rounded-xl px-1 ${heights[rank] || 'pt-1 pb-1'}`}
      data-testid={`rank-card-${rank}`}
    >
      <div className="relative mb-0.5">
        <Avatar name={entry.name} picture={entry.picture} size={avatarSize} isPro={entry.subscription?.plan === 'swell_pro'} />
        <div className={`absolute -bottom-1 -right-1 ${badgeSize} rounded-full flex items-center justify-center font-bold border ${badgeColor}`}>
          {rank}
        </div>
      </div>
      <p className={`${nameSize} font-semibold truncate max-w-full text-center leading-tight ${rank === 1 ? 'text-secondary' : ''}`}>
        {entry.name?.split(' ')[0] || '?'}
      </p>
      {entry.username && (
        <p className={`${subSize} text-muted-foreground/40 truncate max-w-full`}>@{entry.username}</p>
      )}
      <p className={`${subSize} text-muted-foreground/30 font-mono`}>#{entry.user_number || '—'}</p>
    </motion.div>
  );
}

export default function Community({ user }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('feed');
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState({});
  const [activity, setActivity] = useState({ recent_sessions: [], recent_spots: [] });
  const [trending, setTrending] = useState([]);
  const [referralLb, setReferralLb] = useState([]);
  const [verifiedSpots, setVerifiedSpots] = useState([]);
  const [showRankCard, setShowRankCard] = useState(false);
  const [showLbCard, setShowLbCard] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [lbRes, statsRes, actRes, trendRes, refLbRes, verRes] = await Promise.all([
        authFetch(`${API}/api/community/leaderboard`),
        authFetch(`${API}/api/community/stats`),
        authFetch(`${API}/api/community/activity`),
        authFetch(`${API}/api/community/trending-spots`),
        authFetch(`${API}/api/community/referral-leaderboard`),
        authFetch(`${API}/api/community/verified-leaderboard`),
      ]);
      if (lbRes.ok) setLeaderboard((await lbRes.json()).leaderboard || []);
      if (statsRes.ok) setStats(await statsRes.json());
      if (actRes.ok) setActivity(await actRes.json());
      if (trendRes.ok) setTrending((await trendRes.json()).trending || []);
      if (refLbRes.ok) setReferralLb((await refLbRes.json()).leaderboard || []);
      if (verRes.ok) setVerifiedSpots((await verRes.json()).spots || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const podiumColors = ['text-secondary', 'text-muted-foreground', 'text-accent'];

  if (loading) {
    return (
      <PageShell title="Community">
        <div className="flex items-center justify-center py-20">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Community">
      <SEO title="Community" description="Join the SurfSwell.ai community. Compete on leaderboards, track your aura, and connect with surfers." path="/community" />
      <div className="p-5" data-testid="community-page">

        {/* Community Stats Bar */}
        <div className="grid grid-cols-4 gap-2 mb-5" data-testid="community-stats">
          <StatCard icon={Users} value={stats.total_users || 0} label="surfers" color="text-primary" testId="stat-users" />
          <StatCard icon={Waves} value={stats.total_sessions || 0} label="sessions" color="text-secondary" testId="stat-sessions" />
          <StatCard icon={Clock} value={`${stats.total_water_hours || 0}h`} label="water time" color="text-accent" testId="stat-watertime" />
          <StatCard icon={MapPin} value={stats.total_community_spots || 0} label="spots" color="text-muted-foreground" testId="stat-spots" />
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-1 bg-bg-subtle/50 rounded-xl p-1 mb-4" data-testid="community-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2 text-[11px] font-medium transition-all ${
                  active ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {/* FEED TAB */}
          {tab === 'feed' && (
            <motion.div
              key="feed"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
            >
              {/* Recent Sessions */}
              <div className="mb-5">
                <p className="text-[10px] tracking-widest uppercase text-muted-foreground/60 font-bold mb-3">Recent Sessions</p>
                {activity.recent_sessions?.length > 0 ? (
                  <div className="space-y-2">
                    {activity.recent_sessions.map((s, i) => (
                      <motion.div
                        key={s.session_id || i}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.03 }}
                        className="bg-bg-subtle/50 border border-white/5 rounded-xl p-3"
                        data-testid={`activity-session-${i}`}
                      >
                        <div className="flex items-center gap-2.5 mb-1.5">
                          <Avatar name={s.user_name} picture={s.user_picture} size="sm" isPro={s.user_plan === 'swell_pro'} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium truncate">{s.user_name || 'Anonymous'}</span>
                              <span className="text-[9px] text-muted-foreground/40">
                                <TimeAgo dateStr={s.date} />
                              </span>
                            </div>
                          </div>
                          <StarsDisplay rating={s.rating} />
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground pl-8">
                          <MapPin className="w-3 h-3 text-primary/50 flex-shrink-0" />
                          <span className="truncate font-medium">{s.location}</span>
                          {s.wave_height && (
                            <span className="flex-shrink-0 text-muted-foreground/50">{s.wave_height}</span>
                          )}
                          {s.duration_minutes > 0 && (
                            <span className="flex-shrink-0 text-muted-foreground/50 ml-auto">{s.duration_minutes}min</span>
                          )}
                        </div>
                        {s.conditions && (
                          <div className="flex flex-wrap gap-1 mt-1.5 pl-8">
                            {(Array.isArray(s.conditions) ? s.conditions : [s.conditions]).slice(0, 3).map((c, ci) => (
                              <span key={ci} className="text-[8px] bg-white/5 text-muted-foreground/60 rounded-md px-1.5 py-0.5">{c}</span>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-bg-subtle/30 rounded-xl border border-white/[0.03]">
                    <Waves className="w-5 h-5 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-[11px] text-muted-foreground/50">no recent sessions — get out there!</p>
                  </div>
                )}
              </div>

              {/* Recently Added Spots */}
              {activity.recent_spots?.length > 0 && (
                <div>
                  <p className="text-[10px] tracking-widest uppercase text-muted-foreground/60 font-bold mb-3">New Spots Added</p>
                  <div className="space-y-2">
                    {activity.recent_spots.map((spot, i) => (
                      <motion.div
                        key={spot.name || i}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.03 }}
                        className="bg-bg-subtle/50 border border-white/5 rounded-xl p-3 flex items-center gap-3"
                        data-testid={`new-spot-${i}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{spot.name}</p>
                          <div className="flex items-center gap-2 text-[9px] text-muted-foreground/50 mt-0.5">
                            {spot.region && <span>{spot.region}</span>}
                            {spot.difficulty && spot.difficulty !== 'all' && (
                              <span className="bg-white/5 rounded px-1 py-px">{spot.difficulty}</span>
                            )}
                            <span>by {spot.added_by_name || '?'}</span>
                          </div>
                        </div>
                        {(spot.shakas || 0) > 0 && (
                          <div className="text-[10px] text-secondary flex items-center gap-0.5 flex-shrink-0">
                            <span>{spot.shakas}</span>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* LEADERBOARD TAB */}
          {tab === 'leaderboard' && (
            <motion.div
              key="leaderboard"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
            >
              {leaderboard.length > 0 ? (
                <>
                  {/* Top 7 — single row: 6 4 2 1 3 5 7 */}
                  <div className="mb-5 pb-4 border-b border-white/[0.04]" data-testid="rankings-bracket">
                    <div className="flex items-end gap-1.5">
                      {[5, 3, 1, 0, 2, 4, 6].map(idx => {
                        const entry = leaderboard[idx];
                        if (!entry) return null;
                        return <RankCard key={entry.user_id || idx} entry={entry} rank={idx + 1} />;
                      })}
                    </div>
                  </div>

                  {/* Rest of leaderboard (#8+) as clean list */}
                  {leaderboard.length > 7 && (
                    <div data-testid="rankings-list">
                      {/* Column Header */}
                      <div className="grid grid-cols-[2rem_2rem_1fr_5rem] gap-2 px-3 mb-1.5">
                        <span className="text-[8px] text-muted-foreground/30 font-mono uppercase">#</span>
                        <span></span>
                        <span className="text-[8px] text-muted-foreground/30 uppercase">Name</span>
                        <span className="text-[8px] text-muted-foreground/30 uppercase text-right">ID</span>
                      </div>
                      <div className="space-y-1">
                        {leaderboard.slice(7).map((entry, i) => {
                          const rank = i + 8;
                          return (
                            <motion.div
                              key={entry.user_id || rank}
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.02 }}
                              className="grid grid-cols-[2rem_2rem_1fr_5rem] gap-2 items-center bg-bg-subtle/40 hover:bg-bg-subtle/60 border border-white/[0.03] rounded-xl px-3 py-2 transition-colors"
                              data-testid={`leaderboard-entry-${rank}`}
                            >
                              <span className="text-[10px] text-muted-foreground/40 font-mono text-center">{rank}</span>
                              <Avatar name={entry.name} picture={entry.picture} size="sm" isPro={entry.subscription?.plan === 'swell_pro'} />
                              <div className="min-w-0">
                                <p className="text-xs font-medium truncate">{entry.name?.split(' ')[0] || '?'}</p>
                                {entry.username && (
                                  <p className="text-[9px] text-muted-foreground/40 truncate">@{entry.username}</p>
                                )}
                              </div>
                              <span className="text-[10px] text-muted-foreground/50 font-mono text-right">
                                #{entry.user_number || '—'}
                              </span>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Share Your Rank CTA */}
                  {(() => {
                    const myIdx = leaderboard.findIndex(e => e.user_id === user?.user_id);
                    if (myIdx === -1) return null;
                    return (
                      <motion.button
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        onClick={() => setShowLbCard(true)}
                        className="w-full mt-4 border border-primary/20 hover:border-primary/40 rounded-2xl px-4 py-3 flex items-center justify-center gap-2 transition-all group"
                        style={{ background: 'rgba(0,245,255,0.03)' }}
                        data-testid="share-leaderboard-rank-btn"
                      >
                        <Share2 className="w-3.5 h-3.5 text-primary/60 group-hover:text-primary transition-colors" />
                        <span className="text-xs font-semibold text-primary/70 group-hover:text-primary transition-colors">
                          Share your #{myIdx + 1} ranking
                        </span>
                      </motion.button>
                    );
                  })()}
                </>
              ) : (
                <div className="text-center py-12">
                  <Trophy className="w-6 h-6 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground/50">no surfers on the board yet</p>
                </div>
              )}
            </motion.div>
          )}

          {/* REFERRAL LEADERBOARD TAB */}
          {tab === 'referral' && (
            <motion.div
              key="referral"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
              data-testid="referral-leaderboard-tab"
            >
              <p className="text-[10px] tracking-widest uppercase text-muted-foreground/60 font-bold mb-3">Stoke Spreaders</p>

              {referralLb.length > 0 ? (
                <div className="space-y-2">
                  {referralLb.map((entry, i) => {
                    const rank = i + 1;
                    const medalColors = { 1: 'from-amber-400 to-yellow-500', 2: 'from-slate-300 to-slate-400', 3: 'from-orange-400 to-amber-500' };
                    const isTop3 = rank <= 3;
                    return (
                      <motion.div
                        key={entry.user_id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className={`border rounded-2xl p-3.5 flex items-center gap-3 transition-all ${
                          isTop3
                            ? 'bg-amber-500/[0.04] border-amber-500/15 hover:border-amber-500/25'
                            : 'bg-bg-subtle/50 border-white/5 hover:border-white/10'
                        }`}
                        data-testid={`referral-rank-${rank}`}
                      >
                        {/* Rank badge */}
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-[10px] ${
                          isTop3
                            ? `bg-gradient-to-br ${medalColors[rank]} text-white shadow-sm`
                            : 'bg-white/5 border border-white/10 text-muted-foreground/50'
                        }`}>
                          {rank}
                        </div>

                        {/* Avatar */}
                        <Avatar name={entry.name} picture={entry.picture} size="md" isPro={entry.subscription?.plan === 'swell_pro'} />

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-semibold truncate ${rank === 1 ? 'text-amber-400' : ''}`}>
                            {entry.name?.split(' ')[0] || '?'}
                            {entry.username && <span className="text-muted-foreground/40 font-normal ml-1">@{entry.username}</span>}
                          </p>
                          <div className="flex items-center gap-2.5 text-[9px] text-muted-foreground/50 mt-0.5">
                            <span className="flex items-center gap-0.5">
                              <Users className="w-2.5 h-2.5" />
                              {entry.direct_invites} invite{entry.direct_invites !== 1 ? 's' : ''}
                            </span>
                            <span className="flex items-center gap-0.5">
                              <Zap className="w-2.5 h-2.5" />
                              {entry.referral_events} ripple{entry.referral_events !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>

                        {/* Aura score */}
                        <div className="text-right flex-shrink-0">
                          <p className={`text-sm font-bold ${isTop3 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                            +{entry.total_referral_aura}
                          </p>
                          <p className="text-[8px] text-muted-foreground/40 uppercase tracking-wider">aura</p>
                        </div>
                      </motion.div>
                    );
                  })}

                  {/* Share Your Rank CTA */}
                  {(() => {
                    const myRankIdx = referralLb.findIndex(e => e.user_id === user?.user_id);
                    if (myRankIdx === -1) return null;
                    return (
                      <motion.button
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        onClick={() => setShowRankCard(true)}
                        className="w-full mt-2 border border-amber-500/20 hover:border-amber-500/40 rounded-2xl px-4 py-3 flex items-center justify-center gap-2 transition-all group"
                        style={{ background: 'rgba(251,191,36,0.04)' }}
                        data-testid="share-rank-btn"
                      >
                        <Share2 className="w-3.5 h-3.5 text-amber-400/60 group-hover:text-amber-400 transition-colors" />
                        <span className="text-xs font-semibold text-amber-400/70 group-hover:text-amber-400 transition-colors">
                          Share your #{myRankIdx + 1} rank
                        </span>
                      </motion.button>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-12 bg-bg-subtle/30 rounded-xl border border-white/[0.03]">
                  <Flame className="w-6 h-6 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground/50">no referral aura earned yet</p>
                  <p className="text-[10px] text-muted-foreground/30 mt-1">share your referral link from the Crew page to start spreading the stoke</p>
                </div>
              )}
            </motion.div>
          )}

          {/* HOT SPOTS TAB */}
          {tab === 'spots' && (
            <motion.div
              key="spots"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
            >
              {/* Top Verified Spots Leaderboard */}
              <div className="mb-6" data-testid="verified-leaderboard">
                <div className="flex items-center gap-2 mb-3">
                  <BadgeCheck className="w-4 h-4 text-amber-400" />
                  <p className="text-[10px] tracking-widest uppercase text-muted-foreground/60 font-bold">Top Verified Spots</p>
                </div>
                {verifiedSpots.length > 0 ? (
                  <div className="space-y-1.5">
                    {verifiedSpots.map((spot, i) => {
                      const rank = i + 1;
                      const isTop3 = rank <= 3;
                      const medalBg = { 1: 'from-amber-500/20 to-amber-400/5 border-amber-500/25', 2: 'from-slate-400/15 to-slate-400/5 border-slate-400/20', 3: 'from-orange-500/15 to-orange-400/5 border-orange-500/20' };
                      const medalText = { 1: 'text-amber-400', 2: 'text-slate-300', 3: 'text-orange-400' };
                      return (
                        <motion.button
                          key={spot.name}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.04 }}
                          onClick={() => navigate(`/spots/${encodeURIComponent(spot.name)}`)}
                          className={`w-full text-left rounded-xl p-3 flex items-center gap-3 transition-all group ${
                            isTop3
                              ? `bg-gradient-to-r ${medalBg[rank]} border`
                              : 'bg-bg-subtle/50 border border-white/5 hover:border-white/10'
                          }`}
                          data-testid={`verified-spot-${rank}`}
                        >
                          {/* Rank */}
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${
                            isTop3
                              ? `${medalText[rank]} bg-white/10`
                              : 'text-muted-foreground/40 bg-white/5'
                          }`}>
                            {rank}
                          </div>

                          {/* Spot info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className={`text-sm font-medium truncate group-hover:text-primary transition-colors ${rank === 1 ? 'text-amber-400' : ''}`}>
                                {spot.name}
                              </p>
                              <BadgeCheck className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 drop-shadow-[0_0_3px_rgba(251,191,36,0.4)]" />
                            </div>
                            <div className="flex items-center gap-2 text-[9px] text-muted-foreground/50 mt-0.5">
                              {spot.region && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{spot.region}</span>}
                              {spot.difficulty && spot.difficulty !== 'all' && <span className="bg-white/5 rounded px-1 py-px">{spot.difficulty}</span>}
                              {spot.wave_type && <span>{spot.wave_type}</span>}
                            </div>
                          </div>

                          {/* Shakas count */}
                          <div className="text-right flex-shrink-0">
                            <p className={`text-sm font-bold ${isTop3 ? medalText[rank] : 'text-muted-foreground'}`}>
                              {spot.shakas}
                            </p>
                            <p className="text-[7px] text-muted-foreground/40 uppercase tracking-wider">shakas</p>
                          </div>

                          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/20 group-hover:text-muted-foreground/50 transition-colors flex-shrink-0" />
                        </motion.button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-bg-subtle/30 rounded-xl border border-white/[0.03]">
                    <BadgeCheck className="w-5 h-5 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-[11px] text-muted-foreground/50">no verified spots yet</p>
                    <p className="text-[9px] text-muted-foreground/30 mt-1">spots earn verification at 13+ shakas from the community</p>
                  </div>
                )}
              </div>

              <p className="text-[10px] tracking-widest uppercase text-muted-foreground/60 font-bold mb-3">Trending This Week</p>
              {trending.length > 0 ? (
                <div className="space-y-2">
                  {trending.map((spot, i) => {
                    const heat = spot.session_count >= 10 ? 'text-red-400' : spot.session_count >= 5 ? 'text-orange-400' : spot.session_count >= 2 ? 'text-yellow-400' : 'text-muted-foreground';
                    return (
                      <motion.button
                        key={spot.name || i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04 }}
                        onClick={() => navigate(`/spots/${encodeURIComponent(spot.name)}`)}
                        className="w-full bg-bg-subtle/50 hover:bg-bg-subtle/70 border border-white/5 hover:border-white/10 rounded-xl p-3.5 flex items-center gap-3 transition-all text-left group"
                        data-testid={`trending-spot-${i}`}
                      >
                        <div className="w-8 text-center flex-shrink-0">
                          <TrendingUp className={`w-4 h-4 mx-auto ${heat}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{spot.name}</p>
                          <div className="flex items-center gap-3 text-[9px] text-muted-foreground/50 mt-0.5">
                            <span>{spot.session_count} sessions</span>
                            <span>{spot.surfer_count} surfer{spot.surfer_count !== 1 ? 's' : ''}</span>
                            {spot.avg_rating > 0 && (
                              <span className="flex items-center gap-0.5">
                                <Star className="w-2 h-2 text-secondary fill-secondary" />
                                {spot.avg_rating}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/20 group-hover:text-muted-foreground/50 transition-colors flex-shrink-0" />
                      </motion.button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-bg-subtle/30 rounded-xl border border-white/[0.03]">
                  <TrendingUp className="w-5 h-5 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-[11px] text-muted-foreground/50">no trending spots yet — log some sessions!</p>
                </div>
              )}

              {/* Explore All Spots CTA */}
              <button
                data-testid="explore-all-spots"
                onClick={() => navigate('/spots')}
                className="w-full mt-4 bg-primary/5 border border-primary/10 hover:border-primary/25 rounded-xl px-4 py-3 flex items-center justify-center gap-2 transition-colors group"
              >
                <MapPin className="w-3.5 h-3.5 text-primary/60 group-hover:text-primary transition-colors" />
                <span className="text-xs font-medium text-primary/70 group-hover:text-primary transition-colors">Explore All Spots</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Referral Rank Share Card Modal */}
      <AnimatePresence>
        {showRankCard && (() => {
          const myRankIdx = referralLb.findIndex(e => e.user_id === user?.user_id);
          if (myRankIdx === -1) return null;
          return (
            <ReferralCard
              user={user}
              rank={myRankIdx + 1}
              stats={referralLb[myRankIdx]}
              onClose={() => setShowRankCard(false)}
            />
          );
        })()}
      </AnimatePresence>

      {/* Leaderboard Rank Share Card Modal */}
      <AnimatePresence>
        {showLbCard && (() => {
          const myIdx = leaderboard.findIndex(e => e.user_id === user?.user_id);
          if (myIdx === -1) return null;
          return (
            <LeaderboardCard
              user={user}
              rank={myIdx + 1}
              stats={leaderboard[myIdx]}
              onClose={() => setShowLbCard(false)}
            />
          );
        })()}
      </AnimatePresence>
    </PageShell>
  );
}
